// src/hooks/useNotifications.js
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  useGetAdminNotificationsQuery, 
  useGetOrganizationNotificationsQuery, 
  useGetInterpreterNotificationsQuery 
} from '../store/api/apiSlice';

export const useNotifications = () => {
  const { user, role } = useSelector((state) => state.auth);
  const [notificationCount, setNotificationCount] = useState(0);

  // Query with more frequent polling for real-time updates
  const {
    data: adminNotifications,
    isLoading: adminLoading,
    refetch: refetchAdminNotifications
  } = useGetAdminNotificationsQuery(undefined, {
    skip: role !== 'admin',
    // pollingInterval: 15000, // Poll every 15 seconds
    refetchOnMountOrArgChange: true
  });

  const {
    data: orgNotifications,
    isLoading: orgLoading,
    refetch: refetchOrgNotifications
  } = useGetOrganizationNotificationsQuery(user?.organization?._id, {
    skip: !user?.organization?._id || role !== 'organization',
    // pollingInterval: 15000, // Poll every 15 seconds
    refetchOnMountOrArgChange: true
  });

  const {
    data: interpreterNotifications,
    isLoading: interpreterLoading,
    refetch: refetchInterpreterNotifications
  } = useGetInterpreterNotificationsQuery(user?.interpreter?._id, {
    skip: !user?.interpreter?._id || role !== 'interpreter',
    // pollingInterval: 15000, // Poll every 15 seconds
    refetchOnMountOrArgChange: true
  });

  // Update notification count whenever data changes
  useEffect(() => {
    let count = 0;
    
    switch (role) {
      case 'admin':
        count = adminNotifications?.data?.length || 0;
        break;
      case 'organization':
        count = orgNotifications?.data?.length || 0;
        break;
      case 'interpreter':
        count = interpreterNotifications?.data?.length || 0;
        break;
      default:
        count = 0;
    }
    
    setNotificationCount(count);
  }, [adminNotifications, orgNotifications, interpreterNotifications, role]);

  // Get the appropriate notifications data
  const getNotificationsData = () => {
    switch (role) {
      case 'admin':
        return adminNotifications;
      case 'organization':
        return orgNotifications;
      case 'interpreter':
        return interpreterNotifications;
      default:
        return { data: [] };
    }
  };

  // Force refresh notifications
  const refreshNotifications = () => {
    switch (role) {
      case 'admin':
        return refetchAdminNotifications();
      case 'organization':
        return refetchOrgNotifications();
      case 'interpreter':
        return refetchInterpreterNotifications();
      default:
        return Promise.resolve();
    }
  };

  const isLoading = adminLoading || orgLoading || interpreterLoading;

  return {
    notifications: getNotificationsData(),
    count: notificationCount,
    isLoading,
    refresh: refreshNotifications
  };
};

export default useNotifications;